import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Chevron } from 'components/icons';

interface Props {
  children: ReactNode;
  className?: string;
  defaultOpen?: boolean;
  title: JSX.Element;
  chevronClassName?: string;
  headerClassName?: string;
  headerDivider?: boolean;
  iconOverrides?: {
    maximizeIcon: JSX.Element;
    minimizeIcon: JSX.Element;
  };
  singleExpandControl?: {
    isOpen: boolean;
    onToggle: () => void;
  };
}

export const Accordion = ({
  className,
  children,
  defaultOpen = false,
  title,
  chevronClassName,
  headerClassName,
  headerDivider = false,
  iconOverrides,
  singleExpandControl,
}: Props) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const accordionOpen = singleExpandControl ? singleExpandControl.isOpen : internalOpen;

  const handleToggle = () => {
    if (singleExpandControl) {
      singleExpandControl.onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  const customIcon = iconOverrides ? (accordionOpen ? iconOverrides.minimizeIcon : iconOverrides.maximizeIcon) : null;

  return (
    <div className={className}>
      <button
        onClick={handleToggle}
        className={clsx(
          twMerge(
            'flex w-full items-center justify-between text-start',
            headerDivider && accordionOpen && 'border-b-2 border-grey-100',
            headerClassName,
          ),
        )}
      >
        {title}
        <span>
          {customIcon ?? (
            <Chevron
              className={twMerge(
                clsx(
                  'ml-8 h-24 w-24 rotate-180 transition-transform duration-300 ease-in-out',
                  accordionOpen && 'rotate-0',
                  chevronClassName,
                ),
              )}
            />
          )}
        </span>
      </button>
      <div
        className={clsx(
          'grid overflow-hidden transition-all duration-300 ease-in-out',
          accordionOpen ? 'mt-16 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
};
